// The prenatal scope a clinical record can carry (decision 21): which pregnancy, and which fetus.
//
// Why a shared helper rather than two lines per form. Each note form builds its NEW-record defaults
// from a hand-written whitelist of fields — it does NOT spread `model` (only the isEditing branch
// does). So a form that has not explicitly opted in silently DROPS the scope seeded when the record was
// opened "for this fetus": the record saves, looks fine, and is quietly attributed to the mother
// instead. Nothing fails loudly. One shared spread makes the opt-in uniform, greppable, and testable —
// see prenatalScope.test.js, which fails if a type is offered for a fetus while its form has not opted
// in.

// The note types offered for a fetus. Clinically: a Test is the karyotype or NIPT result, a Sample is
// the amniocentesis or CVS specimen, and a Clinical Note is a free-text observation. A "linked file" in
// REQ-E1 is an attachment ON one of these rather than a type of its own, so it needs no separate entry.
//
// Adding a type here REQUIRES its form to spread prenatalScopeFrom in the new-record branch. The test
// enforces that pairing.
export const PRENATAL_NOTE_TYPES = Object.freeze([
  'Test',
  'Sample',
  'Clinical Note'
]);

// Which form file implements each type — used by the test to check the pairing.
export const PRENATAL_TYPE_FORMS = Object.freeze({
  Test: 'TestForm.jsx',
  Sample: 'SampleForm.jsx',
  'Clinical Note': 'ClinicalNoteForm.jsx'
});

// Spread into a form's new-record defaults. Returns an EMPTY object for an ordinary record, so the
// fields are absent rather than present-and-undefined — nothing to strip, and nothing that could read
// as "explicitly not prenatal".
export const prenatalScopeFrom = (model) => {
  if (!model?.pregnancyId) return {};
  return {
    pregnancyId: model.pregnancyId,
    ...(model.fetusId ? { fetusId: model.fetusId } : {})
  };
};

// Whether a record is about a pregnancy (TGCLOUD-1868 B7). Such a Test is the mother's note, and
// its variants are the fetus's, so they are never copied to her genotypes: the Test form pushes
// none, as the server's copy writes none (backend genotypeVariantService.isPrenatalTest, the same
// rule; backend/__tests__/fixtures/prenatalScopeVectors.json binds the two). Unlike
// prenatalScopeFrom, a fetus without its pregnancy counts: a fetus's result is never the mother's.
// Whether one field of a scope names something: what the upload and the accept send of a scope too
// (useLabReportReview, useClinicalSuggestions), so the three read a scope by one rule.
export const namesScope = (value) =>
  value != null && (typeof value !== 'string' || value.trim() !== '');
export const carriesPrenatalScope = (record) =>
  namesScope(record?.pregnancyId) || namesScope(record?.fetusId);
